'use client';

import { API_ROUTES, useApiPrefetch, useApiQuery } from '@shared/api-utils';
import { useDebounce } from '@shared/utils';
import { useEffect, useState } from 'react';
import type { SearchProps, SearchResult, SearchResultsResponse } from '../types';

const MIN_SEARCH_TERM_LENGTH = 3;
const DEBOUNCE_DELAY_MS = 300;

type UseSearchParams = {
  searchType: SearchProps['searchType'];
};

function useSearch({ searchType }: UseSearchParams) {
  const prefetch = useApiPrefetch();
  useEffect(() => {
    prefetch.prefetch<SearchResultsResponse>(API_ROUTES.SEARCH);
  }, [prefetch]);

  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const debouncedSearch = useDebounce(searchTerm, DEBOUNCE_DELAY_MS);
  const isSearchActive = debouncedSearch.length >= MIN_SEARCH_TERM_LENGTH;

  const { data: searchResults, isLoading } = useApiQuery<SearchResultsResponse>(API_ROUTES.SEARCH);

  useEffect(() => {
    if (searchResults && isSearchActive) {
      let resultsToFilter: SearchResult[] = [];

      if (searchType === 'all') {
        resultsToFilter = [
          // Ordered by weight (Games, Lotteries, Syndicates, Scratchcards)
          ...searchResults.games.map(item => ({ ...item, type: 'games' as const })),
          ...searchResults.lotteries.map(item => ({ ...item, type: 'lotteries' as const })),
          ...searchResults.syndicates.map(item => ({ ...item, type: 'syndicates' as const })),
          ...searchResults.scratchcards.map(item => ({ ...item, type: 'scratchcards' as const })),
        ];
      } else {
        resultsToFilter = [...searchResults[searchType].map(item => ({ ...item, type: searchType }))];
      }

      const filteredResults = resultsToFilter.filter(result => result.name.toLowerCase().includes(debouncedSearch.toLowerCase()));
      // Calling setState synchronously within an effect body causes cascading renders that can hurt performance, and is not recommended. (https://react.dev/learn/you-might-not-need-an-effect).
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setResults(filteredResults);
    } else {
      setResults([]);
    }
  }, [searchResults, debouncedSearch, searchType, isSearchActive]);

  return {
    searchTerm,
    setSearchTerm,
    results,
    isLoading,
    debouncedSearch,
    isSearchActive,
  };
}

export { useSearch };
